/**
 * Sanitizador de Entrada de Dados
 * 
 * Centraliza todas as sanitizações de entrada de dados do usuário,
 * removendo caracteres perigosos, padrões maliciosos e limitando tamanhos.
 * 
 * @module input.sanitizer
 */

import type { SanitizeOptions } from '../types/security.types';
import { INPUT_VALIDATION } from '../validators/input.validator';

/**
 * Sanitiza entrada removendo caracteres e padrões perigosos
 * 
 * Remove da string de entrada:
 * - Tags HTML e scripts (<script>, <iframe>, etc.)
 * - Event handlers (onclick, onerror, etc.)
 * - Protocolos perigosos (javascript:, data:text/html)
 * - Caracteres de controle (0x00-0x1F, 0x7F)
 * - Caracteres especiais perigosos (<, >, ", ', &)
 * 
 * Também aplica:
 * - Trim (remove espaços no início e fim)
 * - Limitação de tamanho
 * 
 * @param input - String de entrada a ser sanitizada
 * @param options - Opções de sanitização (opcional)
 * @returns String sanitizada e segura
 * 
 * @example
 * ```typescript
 * sanitizeInput('<script>alert("xss")</script>hello') // 'hello'
 * sanitizeInput('  user@name  ', { maxLength: 10 }) // 'user@name'
 * sanitizeInput('javascript:alert(1)') // '' (removido)
 * ```
 */
export function sanitizeInput(
  input: string, 
  options?: SanitizeOptions | number
): string {
  // Suporte para assinatura antiga: sanitizeInput(input, maxLength)
  const opts: SanitizeOptions = typeof options === 'number' 
    ? { maxLength: options }
    : (options || {});
  
  const maxLength = opts.maxLength ?? 255;
  const trim = opts.trim !== false; // default true
  const removeControlChars = opts.removeControlChars !== false; // default true
  const removeDangerousPatterns = opts.removeDangerousPatterns !== false; // default true
  
  // Entrada vazia retorna string vazia
  if (!input || typeof input !== 'string') {
    return '';
  }
  
  let sanitized = input;
  
  // Remove padrões perigosos (scripts, tags HTML, event handlers, etc.)
  if (removeDangerousPatterns) {
    INPUT_VALIDATION.dangerousPatterns.forEach(pattern => {
      sanitized = sanitized.replace(pattern, '');
    });
  }
  
  // Remove caracteres proibidos (controle, HTML, etc.)
  if (removeControlChars) {
    sanitized = sanitized.replace(INPUT_VALIDATION.forbiddenChars, '');
  }
  
  // Trim (remove espaços no início e fim)
  if (trim) {
    sanitized = sanitized.trim();
  }
  
  // Limita tamanho
  return sanitized.slice(0, maxLength);
}

/**
 * Remove apenas padrões perigosos (scripts, tags HTML) sem remover outros caracteres
 * 
 * Útil quando você quer manter alguns caracteres especiais mas remover apenas código malicioso.
 * 
 * @param input - String de entrada
 * @returns String com padrões perigosos removidos
 */
export function removeDangerousPatterns(input: string): string {
  if (!input || typeof input !== 'string') {
    return '';
  }
  
  let sanitized = input;
  INPUT_VALIDATION.dangerousPatterns.forEach(pattern => {
    sanitized = sanitized.replace(pattern, '');
  });
  
  return sanitized;
}

/**
 * Remove apenas caracteres de controle sem remover outros caracteres
 * 
 * @param input - String de entrada
 * @returns String com caracteres de controle removidos
 */
export function removeControlCharacters(input: string): string {
  if (!input || typeof input !== 'string') {
    return '';
  }
  
  return input.replace(INPUT_VALIDATION.forbiddenChars, '');
}

